// @flow
import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {LockOutlined, TrendingUpOutlined} from '@material-ui/icons';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {useUser} from '../../auth/utils';
import {useIsFeatureInTier} from '../../../utils/component';
import type {StoreFeature} from '../../../entities/store-feature';
import {GENERIC_MESSAGES, FEATURE_ACCESS_ROUTES} from '../utils/constants';

type Props = {
  feature: StoreFeature,
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
    minHeight: '300px',
    textAlign: 'center',
  },
  messageContainer: {
    backgroundColor: colors.grey100,
    borderRadius: '1rem',
    padding: '2rem',
    maxWidth: 500,
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.grey1000,
    marginBottom: 16,
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
  message: {
    fontSize: 16,
    color: colors.grey800,
    lineHeight: 1.6,
    marginBottom: 32,
    textAlign: 'center',
    fontWeight: 400,
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(123, 57, 255, 0.3)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.purple4,
      boxShadow: '0 6px 16px rgba(123, 57, 255, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(123, 57, 255, 0.3)',
    },
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  icon: {
    fontSize: 48,
    color: colors.primary,
    opacity: 0.8,
  },
  restrictionIcon: {
    fontSize: 48,
    color: colors.grey600,
    opacity: 0.8,
  },
  accessButton: {
    backgroundColor: colors.info,
    color: 'white',
    '&:hover': {
      backgroundColor: '#0082CC',
    },
  },
}));

export default function FeatureAccessBlock({feature}: Props) {
  const classes = useStyles();
  const user = useUser();
  const hasFeature = useIsFeatureInTier(feature);

  if (!user) {
    return null;
  }

  if (hasFeature) {
    return null;
  }

  const isVendor = user.type === 'vendor-user';
  const message = isVendor
    ? GENERIC_MESSAGES.upselling
    : GENERIC_MESSAGES.restriction;

  const handleButtonClick = () => {
    window.location.href = FEATURE_ACCESS_ROUTES.tiersList;
  };

  return (
    <div className={classes.container}>
      <div className={classes.messageContainer}>
        <div className={classes.iconContainer}>
          {isVendor ? (
            <TrendingUpOutlined className={classes.icon} />
          ) : (
            <LockOutlined className={classes.restrictionIcon} />
          )}
        </div>
        <div className={classes.title}>
          No tienes acceso a esta funcionalidad
        </div>
        <div className={classes.message}>{message}</div>
        {isVendor && (
          <CustomButton
            variant="contained"
            className={classes.upgradeButton}
            onClick={handleButtonClick}
            fullWidth
          >
            <span role="img" aria-label="sparkles">
              ✨
            </span>{' '}
            Mejorar Plan
          </CustomButton>
        )}
      </div>
    </div>
  );
}
